import _ from "lodash";
import { useEffect, useState } from "react";
import { Button, Dropdown, DropdownItemProps, Form, FormProps, Input, Modal, Segment, Table } from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { PageData } from "../models/common_model";
import { DropdownOption } from "../models/testing_model";
import { OtherSequenceParametersSettingData } from "../models/testing_setting_model";
import { fieldError, formatDateTime } from "../utils";
import { Page } from "./commons/Page";

export function OtherSequenceParametersComponent(props: {}) {
    const currentUser = useCurrentUser();

    const [searchData, setSearchField] = useFormData<any>({});
    const [page, setSearchData] = useFormConnect<any, PageData<OtherSequenceParametersSettingData>>({}, 'testing_settings/other_sequence_parameters/search');

    const editModalControl = useModalControl<number>();
    const [reloadNumber, setReloadNumber] = useState(0);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => setSearchData(searchData)}>
                    <Form.Group>
                        <Form.Field inline>
                            <Input placeholder="Parameter Name" name="parameter_name" value={searchData['parameter_name'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <Form.Field inline>
                            <Input placeholder="Product" name="product" value={searchData['product'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <Form.Field inline>
                            <Input placeholder="Testing Station" name="testing_station" value={searchData['testing_station'] || ''} onChange={setSearchField} />
                        </Form.Field>
                        <Form.Field inline>
                            <Input placeholder="Order" name="order" value={searchData['order'] || ''} onChange={setSearchField} />
                        </Form.Field>

                        <div><Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Product</th>
                            <th>Testing Station</th>
                            <th>Order</th>
                            <th>Parameter Name</th>
                            <th>Measurement Name</th>
                            <th>Measurement ID</th>
                            <th>Export File Name</th>
                            <th>Created By</th>
                            <th>Created At</th>
                            <th>{currentUser.permissions.testing.setting.other_sequence_parameters.create && <Button color="blue" icon="add" content="Other Sequence Parameter" size="mini" onClick={e => {
                                editModalControl.data = undefined;
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {page?.items.map(obj => {
                            return <tr key={obj.id}>
                                <td>{obj.product}</td>
                                <td>{obj.testing_station}</td>
                                <td>{obj.order}</td>
                                <td>{obj.parameter_name}</td>
                                <td>{obj.measurement_name}</td>
                                <td>{obj.measurement_id}</td>
                                <td>{obj.export_file_name}</td>
                                <td>{obj.created_by_name}</td>
                                <td>{formatDateTime(obj.created_at)}</td>
                                <td>
                                    <Button onClick={e => {
                                        editModalControl.data = obj.id;
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />

                                    {currentUser.permissions.testing.setting.other_sequence_parameters.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(obj.parameter_name, agree => {
                                            if (agree) {
                                                return restRequest('testing_settings/other_sequence_parameters/' + obj.id, null, 'delete').then(() => setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 })));
                                            }
                                            return Promise.resolve();
                                        })
                                    }} color="red" icon="delete" size="mini"></Button>}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={page} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor modalControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></Editor>
    </>
}

function Editor(props: { allRoles?: { id: number, name: string }[], modalControl: ModalControlData<number | undefined>, onFinish: (data: OtherSequenceParametersSettingData) => void }) {
    const { modalControl: modelControl, onFinish } = props;
    const isCreate = !modelControl.data;
    const id = modelControl.data;

    const currentUser = useCurrentUser();
    const [data, setField, setData] = useFormData<OtherSequenceParametersSettingData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [productOptions, setProductOptions] = useState<DropdownItemProps[]>([]);
    const [stationOptions, setStationOptions] = useState<DropdownItemProps[]>([]);
    const [testingResultDropdownOptions, setTestingResultDropdownOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        restRequest<DropdownOption[]>('svn_repository/result_dropdown_options').then(setTestingResultDropdownOptions)
    }, []);

    useEffect(() => {
        const options = [{ key: '', text: 'None', value: '' }, ...testingResultDropdownOptions.map(t => ({ key: t.name, text: t.name, value: t.name }))];
        setProductOptions(options);
        if (!options.find(o => o.value == data.product)) {
            setField(null, { name: 'product', value: '' });
            setField(null, { name: 'testing_station', value: '' });
        }
    }, [testingResultDropdownOptions]);

    useEffect(() => {
        const product = testingResultDropdownOptions.find(t => t.name == data.product);
        let options: DropdownItemProps[];
        if (product) {
            let stations: string[] = [];
            for (const c of product.children) {
                stations = stations.concat(c.children.map(cc => cc.name))
            }
            options = [{ key: '', text: 'None', value: '' }, ..._.uniq(stations).map(s => ({ key: s, text: s, value: s }))];
        } else {
            options = [{ key: '', text: 'None', value: '' }];
        }

        setStationOptions(options);

        if (!options.find(o => o.value == data.testing_station)) {
            setField(null, { name: 'station', value: '' });
        }
    }, [testingResultDropdownOptions, data.product]);


    useEffect(() => {
        setData({ admin: false } as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<OtherSequenceParametersSettingData>('testing_settings/other_sequence_parameters/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<OtherSequenceParametersSettingData>(`testing_settings/other_sequence_parameters/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }


    return <>
        <Modal {...modelControl.props} size="large">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Product & Testing Station </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    <Form.Group widths="equal">
                        <Form.Input label="Product" style={{ minWidth: '10rem' }} required error={fieldError('product', invalidError)}>
                            <Dropdown name="product" value={data.product || ''} onChange={setField} options={productOptions} search selection fluid></Dropdown>
                        </Form.Input>

                        <Form.Input label="Testing Station" style={{ minWidth: '10rem' }} required error={fieldError('testing_station', invalidError)}>
                            <Dropdown name="testing_station" value={data.testing_station || ''} onChange={setField} options={stationOptions} search selection fluid></Dropdown>
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input name="order" value={data.order || ''} onChange={setField} label="Order" error={fieldError('order', invalidError)}></Form.Input>
                        <Form.Input label="Parameter Name" name="parameter_name" value={data.parameter_name} onChange={setField} error={fieldError('parameter_name', invalidError)}></Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Input label="Measurement Name" name="measurement_name" value={data.measurement_name || ''} onChange={setField} error={fieldError('measurement_name', invalidError)}></Form.Input>
                        <Form.Input label="Measurement ID" name="measurement_id" value={data.measurement_id || ''} onChange={setField} error={fieldError('measurement_id', invalidError)}></Form.Input>
                        <Form.Input label="Expore File Name" name="export_file_name" value={data.export_file_name || ''} onChange={setField} error={fieldError('export_file_name', invalidError)}></Form.Input>
                    </Form.Group>
                    {(currentUser.permissions.testing.setting.other_sequence_parameters.create || currentUser.permissions.testing.setting.other_sequence_parameters.update) && <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modalControl.setOpen(false)}>Cancel</Button>
                    </div>}
                </Form>
            </Modal.Content>
        </Modal>
    </>
}